/**
 * @file `RecalibrationPanel` — root component of the `/recalibration`
 *       route (umbrella #1417 Phase 7, part #1433).
 * @module @labcraft/admin/ui/recalibration/recalibration_panel
 *
 * The operator's course-recalibration steering surface, in three
 * stacked steps:
 *
 *   1. **Signal** — read the anonymized mentor-capture aggregate:
 *      per course/part/objective diagnosis+outcome buckets and
 *      wrong-turn firing stats (`useRecalibrationSignal`).
 *   2. **Draft** — "Generate draft" asks the model for a set of
 *      recalibration proposals (`useRecalibrationDraftMutation`). Each
 *      proposal gets an approve/reject checkbox (local state); a new
 *      draft resets the approvals and clears any prior artifact.
 *   3. **Content PR** — "Produce content PR" turns the approved subset
 *      into a deterministic instruction artifact
 *      (`useRecalibrationContentPrMutation`), rendered as a copyable
 *      block the operator applies from a devshell.
 *
 * All data flows through the TanStack hook layer at
 * `../hooks/use_recalibration.ts` — components never call `useQuery` /
 * `useMutation` directly.
 */

import { useCallback, useMemo, useState, type JSX } from 'react'
import {
  useRecalibrationContentPrMutation,
  useRecalibrationDraftMutation,
  useRecalibrationSignal,
} from '../hooks/index.ts'
import type {
  DiagnosisOutcomeStat,
  RecalibrationDraft,
  RecalibrationProposal,
  WrongTurnStat,
} from '../../shared/recalibration_routes.ts'

/** Render the Recalibration route. */
export function RecalibrationPanel(): JSX.Element {
  const signal = useRecalibrationSignal()
  const draft = useRecalibrationDraftMutation()
  const contentPr = useRecalibrationContentPrMutation()

  // Approved proposal indices. Reset whenever a fresh draft lands.
  const [approved, setApproved] = useState<ReadonlySet<number>>(new Set())

  const proposals = draft.data?.proposals ?? null

  const signalError = signal.isError
    ? signal.error.message || 'Could not load the recalibration signal.'
    : null
  const draftError = draft.isError
    ? draft.error.message || 'Draft failed.'
    : null
  const contentPrError = contentPr.isError
    ? contentPr.error.message || 'Could not produce the content PR.'
    : null

  const onGenerate = useCallback(() => {
    contentPr.reset()
    draft.mutate(undefined, {
      onSuccess: d => {
        setApproved(new Set(d.proposals.map((_, i) => i)))
      },
    })
  }, [draft, contentPr])

  const toggle = useCallback((index: number) => {
    setApproved(prev => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }, [])

  const approvedDraft = useMemo<RecalibrationDraft | null>(() => {
    if (!proposals) return null
    return { proposals: proposals.filter((_, i) => approved.has(i)) }
  }, [proposals, approved])

  const onProduce = useCallback(() => {
    if (!approvedDraft || approvedDraft.proposals.length === 0) return
    contentPr.mutate(approvedDraft)
  }, [approvedDraft, contentPr])

  const approvedCount = approvedDraft?.proposals.length ?? 0

  return (
    <div className="panel recalibration-panel">
      <header className="panel-head">
        <div>
          <div className="panel-head__title-row">
            <h1 className="panel-head__title">Recalibration</h1>
            <span className="panel-head__count">
              {proposals ? `${proposals.length} drafted` : '—'}
            </span>
          </div>
          <p className="panel-head__sub">
            // mentor-capture signal → model draft → content-PR instruction
          </p>
        </div>
        <button
          type="button"
          className="lc-btn lc-btn--primary panel-head__action"
          disabled={draft.isPending}
          onClick={onGenerate}>
          {draft.isPending ? 'Generating…' : 'Generate draft'}
        </button>
      </header>

      {signalError !== null && (
        <div className="panel-banner panel-banner--error">
          <b>Could not load the recalibration signal.</b> {signalError}
        </div>
      )}
      {draftError !== null && (
        <div className="panel-banner panel-banner--error">
          <b>Draft failed.</b> {draftError}
        </div>
      )}
      {contentPrError !== null && (
        <div className="panel-banner panel-banner--error">
          <b>Could not produce the content PR.</b> {contentPrError}
        </div>
      )}

      <div className="reco-scroll">
        <SignalSection
          loading={signal.isPending}
          diagnosis={signal.data?.diagnosis ?? []}
          wrongTurns={signal.data?.wrongTurns ?? []}
        />

        {proposals !== null && (
          <section className="reco-section">
            <div className="reco-section__head">
              <h2 className="reco-section__h">Proposals</h2>
              <span className="reco-section__meta">
                {approvedCount} of {proposals.length} approved
              </span>
            </div>
            {proposals.length === 0 ? (
              <div className="empty-block">
                <b>No changes proposed.</b> The current signal warrants no
                recalibration.
              </div>
            ) : (
              <ul className="reco-proposals">
                {proposals.map((p, i) => (
                  <ProposalRow
                    key={i}
                    proposal={p}
                    approved={approved.has(i)}
                    onToggle={() => toggle(i)}
                  />
                ))}
              </ul>
            )}
            {proposals.length > 0 && (
              <div className="reco-actions">
                <button
                  type="button"
                  className="lc-btn lc-btn--primary"
                  disabled={approvedCount === 0 || contentPr.isPending}
                  onClick={onProduce}>
                  {contentPr.isPending
                    ? 'Producing…'
                    : `Produce content PR (${approvedCount})`}
                </button>
              </div>
            )}
          </section>
        )}

        {contentPr.data && <ArtifactBlock artifact={contentPr.data} />}
      </div>
    </div>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Signal section
////////////////////////////////////////////////////////////////////////////////

/** Render both signal tables (diagnosis + wrong-turn firing). */
function SignalSection(props: {
  loading: boolean
  diagnosis: DiagnosisOutcomeStat[]
  wrongTurns: WrongTurnStat[]
}): JSX.Element {
  const { loading, diagnosis, wrongTurns } = props
  const empty = !loading && diagnosis.length === 0 && wrongTurns.length === 0

  return (
    <section className="reco-section">
      <div className="reco-section__head">
        <h2 className="reco-section__h">Capture signal</h2>
        <span className="reco-section__meta">
          {loading
            ? '// loading…'
            : `${diagnosis.length} diagnosis · ${wrongTurns.length} rules`}
        </span>
      </div>

      {empty ? (
        <div className="empty-block">
          <b>No signal captured yet.</b> Mentor-capture is dark by default; the
          aggregate populates once capture is enabled and learners generate
          episodes.
        </div>
      ) : (
        <div className="reco-tables">
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>course</th>
                  <th>part</th>
                  <th>objective</th>
                  <th>disposition</th>
                  <th>outcome</th>
                  <th>error class</th>
                  <th>count</th>
                </tr>
              </thead>
              <tbody>
                {diagnosis.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="table-empty">
                      // no diagnosis buckets
                    </td>
                  </tr>
                ) : (
                  diagnosis.map((d, i) => (
                    <tr key={i}>
                      <td className="cell-code">{d.course}</td>
                      <td className="cell-meta">{d.part}</td>
                      <td className="cell-code">{d.objectiveId}</td>
                      <td className="cell-meta">{d.disposition}</td>
                      <td>
                        <span
                          className={`spill ${d.outcome === 'RECOVERED' ? 'spill--ok' : 'spill--idle'}`}>
                          {d.outcome}
                        </span>
                      </td>
                      <td className="cell-meta">{d.errorClass ?? '—'}</td>
                      <td className="cell-meta">{d.count}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>course</th>
                  <th>part</th>
                  <th>objective</th>
                  <th>rule</th>
                  <th>origin</th>
                  <th>firings</th>
                  <th>last fired</th>
                </tr>
              </thead>
              <tbody>
                {wrongTurns.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="table-empty">
                      // no wrong-turn firings
                    </td>
                  </tr>
                ) : (
                  wrongTurns.map((w, i) => (
                    <tr key={i}>
                      <td className="cell-code">{w.course}</td>
                      <td className="cell-meta">{w.part}</td>
                      <td className="cell-code">{w.objectiveId}</td>
                      <td className="cell-code">{w.wrongTurnId}</td>
                      <td className="cell-meta">{w.origin}</td>
                      <td className="cell-meta">{w.firings}</td>
                      <td className="cell-meta">
                        {w.lastFiredAt.slice(0, 10)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Proposal row
////////////////////////////////////////////////////////////////////////////////

/** One drafted proposal with an approve/reject checkbox. */
function ProposalRow(props: {
  proposal: RecalibrationProposal
  approved: boolean
  onToggle: () => void
}): JSX.Element {
  const { proposal: p, approved, onToggle } = props
  return (
    <li
      className={`reco-proposal ${approved ? '' : 'reco-proposal--rejected'}`}>
      <label className="reco-proposal__check">
        <input
          type="checkbox"
          className="reco-check"
          checked={approved}
          onChange={onToggle}
          aria-label={
            approved
              ? 'Approved — click to reject'
              : 'Rejected — click to approve'
          }
        />
      </label>
      <div className="reco-proposal__body">
        <div className="reco-proposal__head">
          <span className="spill spill--info">{p.recalibrationClass}</span>
          <span className="cell-code">
            {p.course}/{p.part}
          </span>
          <span className="cell-meta">{p.objectiveId}</span>
          {p.targetRuleId !== null && (
            <span className="cell-code">rule: {p.targetRuleId}</span>
          )}
        </div>
        <p className="reco-proposal__rationale">{p.rationale}</p>
        <p className="reco-proposal__change">{p.proposedChange}</p>
      </div>
    </li>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Artifact block
////////////////////////////////////////////////////////////////////////////////

/** The produced content-PR artifact, rendered as a copyable block. */
function ArtifactBlock(props: {
  artifact: { title: string; body: string }
}): JSX.Element {
  const { artifact } = props
  const [copied, setCopied] = useState(false)
  const text = `${artifact.title}\n\n${artifact.body}`

  const onCopy = useCallback(() => {
    void navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
      },
      () => {
        /* clipboard denied — the operator can still select the text */
      },
    )
  }, [text])

  return (
    <section className="reco-section">
      <div className="reco-section__head">
        <h2 className="reco-section__h">Content PR</h2>
        <button type="button" className="lc-btn lc-btn--ghost" onClick={onCopy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="reco-artifact__title">{artifact.title}</div>
      <pre className="reco-artifact__body">{artifact.body}</pre>
    </section>
  )
}
